import { RefreshCw, ShieldCheck, X } from "lucide-react"
import type { SmartOLTUnconfiguredOnuRow } from "../../../services/smartolt.service.ts"

export type AuthorizeModalState = {
  onu: SmartOLTUnconfiguredOnuRow
  presets: Array<{ id?: string; preset_id?: string; name?: string }>
  loadingPresets: boolean
  selectedPreset: string
  onuExternalId: string
  authorizing: boolean
}

type Props = {
  state: AuthorizeModalState
  onClose: () => void
  onChange: (next: Partial<AuthorizeModalState>) => void
  onSubmit: () => void
}

export function SmartOLTUnconfiguredAuthorizeModal({ state, onClose, onChange, onSubmit }: Props) {
  const { onu } = state

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-xs">
      <div className="w-full max-w-lg rounded-xl border border-slate-200 bg-white p-5 shadow-2xl animate-modal">
        <div className="flex items-center justify-between border-b border-slate-200 pb-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 border border-blue-200">
              <ShieldCheck className="h-4 w-4 text-blue-600" />
            </div>
            <h3 className="text-base font-semibold text-slate-900">Authorize ONU</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-3.5 space-y-1.5 rounded-xl border border-slate-200 bg-slate-50/70 p-3.5 font-mono text-xs text-slate-700">
          <div className="flex justify-between">
            <span className="text-slate-500 font-sans font-medium">OLT</span>
            <span className="font-semibold text-slate-900">{onu.olt_name || onu.olt_id}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500 font-sans font-medium">Board / Port / ONU#</span>
            <span className="text-blue-600 font-semibold">
              {onu.board}/{onu.port}/{onu.onu_number}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500 font-sans font-medium">SN / MAC</span>
            <span className="text-slate-900 font-semibold">{onu.sn || "—"}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500 font-sans font-medium">PON / Type</span>
            <span className="text-blue-700 font-semibold uppercase">
              {(onu.pon_type || "gpon").toUpperCase()}
              {onu.onu_type_name ? ` · ${onu.onu_type_name}` : ""}
            </span>
          </div>
        </div>

        <div className="mt-4 space-y-3.5">
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-slate-700">Authorization preset</label>
            {state.loadingPresets ? (
              <div className="flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 text-xs text-slate-500">
                <RefreshCw className="h-3.5 w-3.5 animate-spin text-blue-600" />
                Loading presets…
              </div>
            ) : state.presets.length > 0 ? (
              <select
                value={state.selectedPreset}
                onChange={(e) => onChange({ selectedPreset: e.target.value })}
                className="h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {state.presets.map((preset) => {
                  const id = String(preset.id || preset.preset_id || "")
                  return (
                    <option key={id} value={id}>
                      {preset.name ? `${preset.name} (${id})` : id}
                    </option>
                  )
                })}
              </select>
            ) : (
              <input
                value={state.selectedPreset}
                onChange={(e) => onChange({ selectedPreset: e.target.value })}
                placeholder="Preset ID"
                className="h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 placeholder-slate-400 shadow-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            )}
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-slate-700">Unique external ID</label>
            <input
              value={state.onuExternalId}
              onChange={(e) => onChange({ onuExternalId: e.target.value })}
              className="h-9 w-full rounded-lg border border-slate-200 bg-white px-3 font-mono text-sm text-slate-900 shadow-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2.5 border-t border-slate-200 pt-3.5">
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 shadow-xs transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={state.authorizing || !state.selectedPreset || !state.onuExternalId.trim()}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-blue-600 px-5 text-sm font-semibold text-white shadow-xs hover:bg-blue-700 disabled:opacity-50 transition-all"
          >
            {state.authorizing ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
            Authorize
          </button>
        </div>
      </div>
    </div>
  )
}
